import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader } from "./DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, Upload, Database, AlertTriangle, ExternalLink, Trash2, MapPin, FileText, Sparkles, Activity, Workflow } from "lucide-react";
import { toast } from "sonner";
import PlaybookAndScratchpad from "../components/PlaybookAndScratchpad";
import DossierViewer from "../components/DossierViewer";

const PIPELINE_BADGE = {
  IDLE:           { label: "—",              className: "bg-zinc-100 text-zinc-500 border border-zinc-300" },
  PARSING:        { label: "PARSING",        className: "bg-amber-100 text-amber-900 border border-amber-300 animate-pulse" },
  AUDITING_SITE:  { label: "AUDITING SITE",  className: "bg-blue-100 text-blue-900 border border-blue-300 animate-pulse" },
  WRITING_PITCH:  { label: "WRITING PITCH",  className: "bg-purple-100 text-purple-900 border border-purple-300 animate-pulse" },
  READY:          { label: "READY",          className: "bg-emerald-600 text-white border border-emerald-700" },
  FAILED:         { label: "FAILED",         className: "bg-red-600 text-white border border-red-700" },
};
const ACTIVE_STATES = new Set(["PARSING", "AUDITING_SITE", "WRITING_PITCH"]);

function riskColor(score) {
  if (score >= 80) return "bg-red-600 text-white";
  if (score >= 60) return "bg-amber-500 text-zinc-950";
  return "bg-emerald-600 text-white";
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="border border-zinc-950 p-5 bg-white" data-testid={testid}>
      <div className="label-xs">{label}</div>
      <div className={`font-display text-3xl md:text-4xl font-black mt-2 tracking-tighter ${accent || "text-zinc-950"}`}>
        {value}
      </div>
    </div>
  );
}

export default function LawsuitRadar() {
  const navigate = useNavigate();
  const [lawsuits, setLawsuits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const [dossierLawsuit, setDossierLawsuit] = useState(null);
  const [dossierOpen, setDossierOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/lawsuits", { params: { limit: 500 } }),
        api.get("/lawsuits/stats"),
      ]);
      setLawsuits(a.data);
      setStats(b.data);
    } catch (e) {
      toast.error("Failed to load lawsuit feed");
    } finally {
      setLoading(false);
    }
  };

  // Light-touch poll: only when at least one lawsuit is in a non-terminal pipeline state.
  const anyActive = useMemo(
    () => lawsuits.some((l) => ACTIVE_STATES.has(l.pipeline_status)),
    [lawsuits]
  );

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!anyActive) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/lawsuits", { params: { limit: 500 } });
        setLawsuits(r.data);
      } catch {}
    }, 3000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [anyActive]);

  const seed = async () => {
    setBusy(true);
    try {
      const r = await api.post("/lawsuits/seed");
      toast.success(`Seeded ${r.data.inserted} demo lawsuits`);
      await load();
    } finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/lawsuits/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.inserted} / ${r.data.total_rows} rows`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} parse errors`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/lawsuits/${id}`);
      setLawsuits((xs) => xs.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch { toast.error("Failed"); }
  };

  const filtered = lawsuits.filter((l) => {
    const q = filter.toLowerCase().trim();
    if (!q) return true;
    return [l.business_name, l.case_number, l.jurisdiction, l.plaintiff_law_firm, l.niche, l.zip_code]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  const openInGeoBlitz = (zip) => {
    if (!zip) {
      toast.warning("This lawsuit has no zip code on file");
      return;
    }
    navigate(`/geo-blitz?zip=${encodeURIComponent(zip)}`);
  };

  const openDossier = (lawsuit) => {
    setDossierLawsuit(lawsuit);
    setDossierOpen(true);
  };

  const runPipeline = async (l) => {
    if (!l.website_url) {
      toast.error("Lawsuit has no website_url to audit");
      return;
    }
    try {
      const r = await api.post(`/lawsuits/${l.id}/run-pipeline`);
      setLawsuits((xs) => xs.map((x) => (x.id === l.id ? r.data : x)));
      toast.success(`Pipeline (re)started for ${l.business_name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start pipeline");
    }
  };

  return (
    <div data-testid="lawsuit-radar-page">
      <PageHeader
        eyebrow="Module 01 · Lawsuit Radar"
        title="High-velocity ADA lawsuit feed"
        description="Federal and state digital-accessibility filings sorted by risk score. Use the radar to identify hunting grounds and affected niches."
        testid="lawsuit-radar-header"
        actions={
          <>
            <input
              type="file"
              accept=".csv,.json"
              ref={fileRef}
              onChange={onUpload}
              className="hidden"
              data-testid="lawsuit-import-input"
            />
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              className="rounded-sm border-zinc-950 text-zinc-950 font-bold uppercase tracking-wide h-10"
              data-testid="lawsuit-import-button"
              disabled={busy}
            >
              <Upload className="w-4 h-4 mr-2" /> Import CSV/JSON
            </Button>
            <Button
              onClick={seed}
              disabled={busy}
              className="rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-bold uppercase tracking-wide h-10"
              data-testid="lawsuit-seed-button"
            >
              <Database className="w-4 h-4 mr-2" /> Seed Demo
            </Button>
          </>
        }
      />

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6">
        {/* National Court Sourcing Playbook + Smart Ingest Scratchpad */}
        <PlaybookAndScratchpad onLawsuitAdded={load} />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat label="Total tracked" value={stats?.total ?? "—"} testid="stat-total" />
          <Stat label="High risk (≥75)" value={stats?.high_risk ?? "—"} accent="text-red-600" testid="stat-high-risk" />
          <Stat label="Top niche" value={stats?.top_niches?.[0]?.niche || "—"} testid="stat-top-niche" />
          <Stat
            label="Top firm count"
            value={stats?.top_niches?.[0]?.count ?? "—"}
            accent="text-blue-700"
            testid="stat-top-niche-count"
          />
        </div>

        {/* Niches */}
        {stats?.top_niches?.length > 0 && (
          <div className="mb-6">
            <div className="label-xs mb-2">Affected niches</div>
            <div className="flex flex-wrap gap-2" data-testid="lawsuit-niches">
              {stats.top_niches.map((n) => (
                <span key={n.niche} className="px-3 py-1.5 border border-zinc-950 text-xs font-bold uppercase tracking-wider bg-white hover:bg-zinc-950 hover:text-white transition-colors duration-150">
                  {n.niche} <span className="font-mono text-zinc-500 ml-1">{n.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="mb-3 flex items-center gap-3">
          <Input
            placeholder="Filter by business, jurisdiction, firm, niche, case #…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-md rounded-sm border-zinc-300 font-mono text-sm h-10"
            data-testid="lawsuit-filter-input"
          />
          <span className="text-xs text-zinc-500 font-mono">{filtered.length} / {lawsuits.length}</span>
        </div>

        {/* Table */}
        <div className="border border-zinc-950 overflow-hidden" data-testid="lawsuit-table-wrapper">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-zinc-950 text-white">
                  <th className="text-left px-3 py-3 font-bold uppercase tracking-wider text-[10px]">Risk</th>
                  <th className="text-left px-3 py-3 font-bold uppercase tracking-wider text-[10px]">Case #</th>
                  <th className="text-left px-3 py-3 font-bold uppercase tracking-wider text-[10px]">Filed</th>
                  <th className="text-left px-3 py-3 font-bold uppercase tracking-wider text-[10px]">Jurisdiction</th>
                  <th className="text-left px-3 py-3 font-bold uppercase tracking-wider text-[10px]">Business</th>
                  <th className="text-left px-3 py-3 font-bold uppercase tracking-wider text-[10px]">Zip Code</th>
                  <th className="text-left px-3 py-3 font-bold uppercase tracking-wider text-[10px]">Plaintiff Firm</th>
                  <th className="text-left px-3 py-3 font-bold uppercase tracking-wider text-[10px]">Niche</th>
                  <th className="text-left px-3 py-3 font-bold uppercase tracking-wider text-[10px]">Pipeline Status</th>
                  <th className="text-right px-3 py-3 font-bold uppercase tracking-wider text-[10px]">Site</th>
                  <th className="text-right px-3 py-3 font-bold uppercase tracking-wider text-[10px]">Dossier</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={13} className="text-center py-10 text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={13} className="text-center py-12">
                      <AlertTriangle className="w-6 h-6 inline text-zinc-400 mr-2" />
                      <span className="text-zinc-500">No lawsuits. Click "Seed Demo" or import a CSV.</span>
                    </td>
                  </tr>
                )}
                {filtered.map((l, idx) => (
                  <tr key={l.id} className="border-t border-zinc-200 hover:bg-zinc-50 row-anim" style={{ animationDelay: `${idx * 12}ms` }} data-testid={`lawsuit-row-${l.id}`}>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-1 text-xs font-black font-mono ${riskColor(l.risk_score)}`}>
                        {l.risk_score}
                      </span>
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-zinc-700">{l.case_number}</td>
                    <td className="px-3 py-3 font-mono text-xs">{l.filing_date}</td>
                    <td className="px-3 py-3 text-xs font-semibold">{l.jurisdiction}</td>
                    <td className="px-3 py-3 font-semibold text-zinc-950">{l.business_name}</td>
                    <td className="px-3 py-3">
                      {l.zip_code ? (
                        <button
                          type="button"
                          onClick={() => openInGeoBlitz(l.zip_code)}
                          title="Open in Geo-Blitz Console"
                          className="inline-flex items-center gap-1 font-mono text-xs font-bold text-blue-700 hover:text-blue-900 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-1"
                          data-testid={`lawsuit-zip-link-${l.id}`}
                        >
                          <MapPin className="w-3 h-3" /> {l.zip_code}
                        </button>
                      ) : (
                        <span className="text-zinc-400 font-mono text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs">{l.plaintiff_law_firm}</td>
                    <td className="px-3 py-3 text-xs uppercase tracking-wider text-zinc-600">{l.niche || "—"}</td>
                    <td className="px-3 py-3">
                      {(() => {
                        const status = l.pipeline_status || "IDLE";
                        const meta = PIPELINE_BADGE[status] || PIPELINE_BADGE.IDLE;
                        const isActive = ACTIVE_STATES.has(status);
                        return (
                          <div className="flex items-center gap-1.5" data-testid={`pipeline-status-${l.id}`}>
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider font-mono ${meta.className}`}
                              data-pipeline-status={status}
                            >
                              {isActive && <Activity className="w-3 h-3" />}
                              {meta.label}
                            </span>
                            {l.website_url && status === "IDLE" && (
                              <button
                                type="button"
                                onClick={() => runPipeline(l)}
                                title="Run automated outreach pipeline"
                                className="text-zinc-400 hover:text-zinc-950"
                                data-testid={`pipeline-run-${l.id}`}
                              >
                                <Workflow className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {status === "FAILED" && (
                              <button
                                type="button"
                                onClick={() => runPipeline(l)}
                                title={l.pipeline_error || "Retry pipeline"}
                                className="text-red-500 hover:text-red-700 underline text-[10px] font-bold"
                                data-testid={`pipeline-retry-${l.id}`}
                              >
                                retry
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {l.website_url && (
                        <a href={l.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline text-xs font-mono">
                          visit <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {(() => {
                        const status = l.pipeline_status || "IDLE";
                        if (status === "READY" && l.pipeline_dossier) {
                          return (
                            <button
                              type="button"
                              onClick={() => openDossier(l)}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-950 text-white hover:bg-blue-700 transition-colors duration-150 text-[10px] font-bold uppercase tracking-wider"
                              data-testid={`open-dossier-${l.id}`}
                            >
                              <Sparkles className="w-3 h-3" /> Print Pitch Dossier
                            </button>
                          );
                        }
                        if (ACTIVE_STATES.has(status)) {
                          return (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-400 text-[10px] font-bold uppercase tracking-wider animate-pulse"
                              data-testid={`dossier-skeleton-${l.id}`}
                              aria-busy="true"
                            >
                              <Loader2 className="w-3 h-3 animate-spin" />
                              <span className="inline-block w-16 h-2 bg-zinc-300 rounded-sm" />
                            </span>
                          );
                        }
                        return <span className="text-zinc-400 font-mono text-[10px]" data-testid={`dossier-na-${l.id}`}>—</span>;
                      })()}
                    </td>
                    <td className="px-3 py-3">
                      <button
                        onClick={() => remove(l.id)}
                        className="text-zinc-400 hover:text-red-600"
                        data-testid={`lawsuit-delete-${l.id}`}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <DossierViewer
        open={dossierOpen}
        onOpenChange={setDossierOpen}
        lawsuit={dossierLawsuit}
      />
    </div>
  );
}
